import { marked } from "marked";
import DOMPurify from "isomorphic-dompurify";
import { publicResponsiveSet } from "./imageOptimization";

marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Custom image renderer for CMS markdown.
 *
 * Editor authoring stays unchanged — they write `![alt](url)` and the
 * MediaBrowser pastes the same `cms-media` public URL it always has.
 *
 * Render-time additions (transparent to the editor):
 *   * `loading="lazy"` + `decoding="async"` for every image
 *   * `srcSet` (480/800/1200w) + `sizes` for Supabase public URLs (cms-media)
 *   * Layout-clamping `responsive-cms-image` class so even an unsized
 *     hero shot doesn't blow out the column width
 *   * Foreign URLs (anything not on Supabase storage) get the lazy hints
 *     but no transform — we never proxy third-party images.
 *
 * If image transformations aren't enabled on the project, the browser
 * still falls back to the bare `src` so editors keep getting working
 * pages — same resilience pattern Modules 25 already established.
 */
function imageRenderer({ href, title, text }) {
  if (!href) return "";
  const safeAlt = escapeHtml(text || "");
  const safeTitle = title ? ` title="${escapeHtml(title)}"` : "";
  const responsive = publicResponsiveSet(href, { widths: [480, 800, 1200], quality: 75 });

  const sizesAttr = ` sizes="(min-width: 768px) 768px, 100vw"`;
  if (responsive) {
    return (
      `<img class="responsive-cms-image" alt="${safeAlt}"${safeTitle}` +
      ` loading="lazy" decoding="async"` +
      ` src="${escapeHtml(responsive.src)}" srcset="${escapeHtml(responsive.srcSet)}"${sizesAttr} />`
    );
  }
  // Foreign / non-Supabase URLs (or render endpoint disabled) — keep
  // the original href; we still apply lazy + decoding for the browser.
  return (
    `<img class="responsive-cms-image" alt="${safeAlt}"${safeTitle}` +
    ` loading="lazy" decoding="async"` +
    ` src="${escapeHtml(href)}" />`
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

marked.use({ renderer: { image: imageRenderer } });

// DOMPurify needs to keep `srcset` + `sizes` + `loading` + `decoding`.
// The default html profile already allows them, but we belt-and-suspender
// them here to defend against future profile updates.
const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ["srcset", "sizes", "loading", "decoding"],
};

/**
 * Turn markdown into safe HTML. Falls back gracefully on parse errors
 * so a malformed CMS body can never crash the public site.
 *
 * Usage:  <div dangerouslySetInnerHTML={{ __html: renderMarkdown(md) }} />
 */
export function renderMarkdown(md) {
  if (!md) return "";
  // Normalize any literal backslash-n / backslash-r sequences (happens when
  // the CMS body was stored as a JSON-escaped string). Converting them to
  // real newlines lets `marked` detect paragraphs/headings properly.
  const normalized = String(md)
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "  ");
  try {
    const raw = marked.parse(normalized);
    return DOMPurify.sanitize(raw, SANITIZE_CONFIG);
  } catch {
    return normalized
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .split(/\n\s*\n/)
      .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
      .join("");
  }
}
